import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useFormContext } from '@context/FormContext'
import { get } from 'lodash'

const PhoneNumber = ({ field, title }) => {
  const { values, errors, submitCount, setFieldValue, setFieldError, setFieldTouched } = useFormContext()
  
  const fieldName = `contactInfo.${field}`
  const fieldValue = get(values, fieldName) || ''
  const fieldError = get(errors, fieldName)

  // Clear error when phone number becomes valid
  useEffect(() => {
    // Always check if the phone is valid when fieldValue changes, regardless of current error state
    if (fieldValue) {
      // Match the validation logic exactly: remove +1 first, then remove all non-word characters
      const cleanedNumber = fieldValue
        ?.replace('+1', '')
        ?.replace(/[\W_]/g, '') || ''
      
      // Check if phone is valid using the same logic as the validation schema
      const isValid = cleanedNumber.length === 10 && cleanedNumber[0] !== '1'
      
      // Clear error if phone is valid (always check, not just when error exists)
      if (isValid && fieldError) {
        setFieldError(fieldName, undefined)
      }
    }
  }, [fieldValue, fieldName, setFieldError, fieldError])

  const handlePhoneNumberChange = (event) => {
    let { value } = event.target

    // Only allow digits, spaces, parentheses, hyphens, and + sign
    // Remove any non-allowed characters
    value = value.replace(/[^\d\s()\-+]/g, '')

    if (value[0] === '+' && value[1] !== '1' && value.length >= 16) {
      return
    }

    let cleanedDigits = value?.replace(/[\W_]/g, '')
    if (cleanedDigits && cleanedDigits[0] === '1' && cleanedDigits.length === 12) return

    // Format the phone number if we have at least 10 digits
    let formattedNumber = value
    if (value.length >= 10) {
      formattedNumber = formatPhoneNumber(value)
    }

    // Update field value
    setFieldValue(fieldName, formattedNumber)
    setFieldTouched(fieldName, true)
    
    // Immediately check and clear error if phone is now valid
    // Match the validation logic exactly: remove +1 first, then remove all non-word characters
    const cleanedNumber = formattedNumber
      ?.replace('+1', '')
      ?.replace(/[\W_]/g, '') || ''
    
    // Check if phone is valid using the same logic as the validation schema
    const isValid = cleanedNumber.length === 10 && cleanedNumber[0] !== '1'
    
    // Clear error immediately if phone is valid (don't wait for useEffect)
    if (isValid && cleanedNumber) {
      setFieldError(fieldName, undefined)
    }

    if (
      formattedNumber &&
      formattedNumber[0] === '+' &&
      formattedNumber[1] !== '1' &&
      formattedNumber.length >= 16
    ) {
      setFieldTouched(fieldName, true)
      setFieldError(
        fieldName,
        'Please enter a valid US phone number'
      )
      return
    }
  }

  const formatPhoneNumber = (input) => {
    // Remove all non-digit characters from the input
    const cleanedInput = input.replace(/\D/g, '')

    // Format the phone number as +1(###) ###-####
    const phoneMatchWithCountryCode = cleanedInput.match(
      /^(\d{0,1})(\d{0,3})(\d{0,3})(\d{0,4})$/
    )
    if (cleanedInput.length === 11 && phoneMatchWithCountryCode)
      return `+${phoneMatchWithCountryCode[1]}(${phoneMatchWithCountryCode[2]}) ${phoneMatchWithCountryCode[3]}-${phoneMatchWithCountryCode[4]}`

    // Format the phone number as (###) ###-####
    const phoneMatch = cleanedInput.match(/^(\d{3})(\d{0,3})(\d{0,4})$/)
    if (cleanedInput.length === 10 && phoneMatch)
      return `(${phoneMatch[1]}) ${phoneMatch[2]}-${phoneMatch[3]}`

    return input // Return the input as-is if it doesn't match the format
  }

  return (
    <div>
      <label htmlFor={`contactInfo.${field}`} className="form-label">
        {title}
        <span style={{ color: '#dc3545', marginLeft: 2 }}>*</span>
      </label>
      <input
        name={`contactInfo.${field}`}
        id={`contactInfo.${field}`}
        type="tel"
        autoComplete="phone"
        className="form-input"
        onChange={handlePhoneNumberChange}
        onKeyDown={(e) => {
          // Allow: backspace, delete, tab, escape, enter, and special keys
          if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
            // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
            (e.keyCode === 65 && e.ctrlKey === true) ||
            (e.keyCode === 67 && e.ctrlKey === true) ||
            (e.keyCode === 86 && e.ctrlKey === true) ||
            (e.keyCode === 88 && e.ctrlKey === true) ||
            // Allow: home, end, left, right
            (e.keyCode >= 35 && e.keyCode <= 39)) {
            return
          }
          // Only allow: digits, space, parentheses, hyphen, plus
          if ((e.keyCode >= 48 && e.keyCode <= 57) || // 0-9
            (e.keyCode >= 96 && e.keyCode <= 105) || // numpad 0-9
            [32, 40, 41, 45, 43].indexOf(e.keyCode) !== -1) { // space, (, ), -, +
            return
          }
          e.preventDefault()
        }}
        value={fieldValue}
      />
      {fieldError && submitCount > 0 && (
        <div className="error-message" title={fieldError}>
          {fieldError}
        </div>
      )}
    </div>
  )
}

PhoneNumber.propTypes = {
  field: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
}

export default PhoneNumber
